import { t } from 'i18next';
import React from 'react';
import { IconX } from '@tabler/icons-react';
import Modal from '../ui/Modal';
import ActionButton from '../ActionButton';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
  variant?: 'danger' | 'warning' | 'info';
}

/**
 * Canonical DHS confirmation dialog: a gray panel with a white card anchored to
 * its bottom edge.
 *
 * The panel uses the solid gray. The SP app's own dialog reaches for the
 * translucent `rgba(39,39,74,0.0196)` here, but DHS_MODAL_PATTERN.md requires the
 * solid token for modal chrome precisely because a translucent gray lets the
 * backdrop through — over this app's black/50 overlay it would read as dark,
 * not as a light panel.
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onClose,
  loading = false,
  variant = 'info',
}) => {
  const confirmColor =
    variant === 'danger' ? 'error' : variant === 'warning' ? 'warning' : 'primary';

  return (
    // `!w-[420px]`: Modal's legacy branch appends its hardcoded `w-[600px]`
    // after the size class, so `size="sm"` is ignored (see Modal.tsx:88). SP
    // renders its confirmations at `size="sm"`; 600px of panel for one line of
    // text is why this dialog read as oversized.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      className="!w-[420px] !rounded-2xl !bg-ds-bg-secondary"
    >
      <div className="flex flex-col bg-ds-bg-secondary">
        {/* Gray header, no border: the panel colour separates it from the card */}
        <div className="flex h-12 flex-shrink-0 items-center justify-between px-5">
          <h2 className="text-base font-semibold tracking-tight text-ds-text-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            disabled={loading}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-ds-text-secondary transition-colors hover:bg-white hover:text-ds-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <IconX size={16} stroke={1.75} />
          </button>
        </div>

        {/* White card anchored to the bottom: only the top corners are rounded */}
        <div className="overflow-hidden rounded-t-2xl border border-b-0 border-ds-border bg-white">
          <div className="px-5 py-8" data-testid="confirmation-dialog-content">
            <p className="break-words text-sm text-ds-text-primary">{message}</p>
          </div>
          <div
            className="flex justify-end gap-3 border-t border-ds-border px-5 py-2.5"
            data-testid="confirmation-dialog-footer"
          >
            <ActionButton
              label={cancelText ?? t('cancel')}
              onClick={onClose}
              variant="outlined"
              color="inherit"
              disabled={loading}
            />
            <ActionButton
              label={confirmText ?? t('confirm')}
              onClick={onConfirm}
              variant="contained"
              color={confirmColor}
              disabled={loading}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
